import React, { useState } from 'react';
import { 
  Button, 
  Typography, 
  Paper, 
  Box, 
  Tabs, 
  Tab, 
  Card, 
  CardContent, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Chip
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { GroupedActivity, Activity } from './interfaces';

interface Step2GroupedActivitiesProps {
  groupedActivities: GroupedActivity[];
  onNext: (projectName: string, groupData: GroupedActivity['groups'][0]) => void;
  onBack: () => void;
}

export const Step2GroupedActivities: React.FC<Step2GroupedActivitiesProps> = ({
  groupedActivities,
  onNext,
  onBack,
}) => {
  const [selectedProjectIndex, setSelectedProjectIndex] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [selectedActivities, setSelectedActivities] = useState<Activity[]>([]);
  const [selectedGroupName, setSelectedGroupName] = useState<string>('');

  const handleGroupClick = (groupData: GroupedActivity['groups'][0], projectName: string) => {
    onNext(projectName, groupData);
  };

  const handleViewActivities = (e: React.MouseEvent, groupData: GroupedActivity['groups'][0]) => {
    e.stopPropagation(); // Prevenir que se active el onClick de la card
    setSelectedActivities(groupData.activities);
    setSelectedGroupName(groupData.groupAssigned);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedActivities([]);
    setSelectedGroupName('');
  };

  const getActivityStatus = (activity: Activity) => {
    if (!activity.shouldHasCardsLoaded) {
      return { label: 'No espera carta', color: '#e0e0e0' }; // Gris claro
    }
    if (activity.hasCardsLoaded) {
      return { label: 'Tiene carta', color: '#4caf50' }; // Verde
    }
    return { label: 'Espera carta', color: '#ffb74d' }; // Naranja clarito
  };

  const selectedProject = groupedActivities[selectedProjectIndex];

  return (
    <Grid container className="dyn-cards-wizard-container" spacing={2}>
      <Paper className="dyn-cards-wizard-paper" elevation={2} sx={{ width: '100%' }}>
        <Typography className="dyn-cards-wizard-title" sx={{ fontSize: 16, fontWeight: 600, mb: 1.5 }}>
          PASO 2: SELECCIONAR CUADRILLA
        </Typography>

        {groupedActivities.length > 1 && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1.5 }}>
            <Tabs
              value={selectedProjectIndex}
              onChange={(_, newValue) => setSelectedProjectIndex(newValue)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {groupedActivities.map((project, index) => (
                <Tab key={index} label={project.projectName} />
              ))}
            </Tabs>
          </Box>
        )}

        {selectedProject && (
          <Box
            className="dyn-cards-groups-container"
            sx={{
              maxHeight: 'calc(100vh - 350px)',
              overflowY: 'auto',
              overflowX: 'hidden',
              p: 1,
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#888',
                borderRadius: '4px',
                '&:hover': {
                  background: '#555',
                },
              },
            }}
          >
            <Grid container spacing={1.5}>
              {selectedProject.groups.map((group, index) => (
              <Grid key={index} size={{ xs: 12, sm: 6, md: 4 }}>
                <Card
                  className="dyn-cards-group-card"
                  sx={{
                    cursor: 'pointer',
                    border: '2px solid #e0e0e0',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.2s ease-in-out',
                    maxWidth: '100%',
                    height: '100%',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      borderColor: 'primary.main',
                      transform: 'translateY(-2px)',
                    },
                  }}
                  onClick={() => handleGroupClick(group, selectedProject.projectName)}
                >
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    {/* Cuadrilla en la parte superior izquierda */}
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        Cuadrilla:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.85rem' }}>
                        {group.groupAssigned}
                      </Typography>
                    </Box>

                    {/* Ingenieros en el centro */}
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        mb: 1, 
                        textAlign: 'center', 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        minHeight: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {group.engineers}
                    </Typography>

                    {/* Estadísticas en la parte inferior */}
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.75,
                        mt: 1,
                        pt: 1,
                        borderTop: '1px solid #e0e0e0',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          Total de actividades:
                        </Typography>
                        <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.9rem' }}>
                          {group.totalActivities}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          Cartas esperadas:
                        </Typography>
                        <Typography variant="body2" fontWeight="bold" color="warning.main" sx={{ fontSize: '0.9rem' }}>
                          {group.shouldHaveCards}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          Cartas cargadas:
                        </Typography>
                        <Typography variant="body2" fontWeight="bold" color="success.main" sx={{ fontSize: '0.9rem' }}>
                          {group.hasCards}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Botón para ver actividades */}
                    <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px solid #e0e0e0' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        fullWidth
                        onClick={(e) => handleViewActivities(e, group)}
                        sx={{ 
                          fontSize: '0.75rem',
                          textTransform: 'none',
                          py: 0.5
                        }}
                      >
                        Ver Tareas
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              ))}
            </Grid>
          </Box>
        )}

        <Grid container spacing={2} justifyContent="flex-start" sx={{ mt: 1.5 }}>
          <Button variant="outlined" onClick={onBack}>
            Atrás
          </Button>
        </Grid>
      </Paper>

      {/* Modal de actividades */}
      <Dialog 
        open={openModal} 
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" component="div">
            Tareas - Cuadrilla: {selectedGroupName}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <List sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {selectedActivities.map((activity, index) => {
              const status = getActivityStatus(activity);
              return (
                <ListItem
                  key={activity.id || index}
                  sx={{
                    bgcolor: status.color,
                    mb: 1,
                    borderRadius: 1,
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" fontWeight="medium">
                          {activity.locationName || `Actividad ${index + 1}`}
                        </Typography>
                        <Chip 
                          label={status.label} 
                          size="small"
                          sx={{
                            bgcolor: 'rgba(255, 255, 255, 0.8)',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                          }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        <Typography variant="caption" display="block">
                          Fecha: {activity.executedDate || 'N/A'}
                        </Typography>
                        <Typography variant="caption" display="block">
                          Usuario: {activity.executedUser || 'N/A'}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

